import React from 'react';
import { CheckIcon, SparklesIcon, DownloadIcon, ShieldCheckIcon, LockIcon } from 'lucide-react';
import { FIELDS } from './fields';
import { SignaturePad } from './SignaturePad';
type Props = {
  values: Record<string, string>;
  signatureDataUrl: string | null;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  onChange: (id: string, value: string) => void;
  onSignatureChange: (url: string | null) => void;
  onDownload: () => void;
  filled: number;
  total: number;
};
export function FormPanel({
  values,
  signatureDataUrl,
  activeId,
  setActiveId,
  onChange,
  onSignatureChange,
  onDownload,
  filled,
  total
}: Props) {
  const sections = Array.from(new Set(FIELDS.map(f => f.section)));
  const allFilled = filled === total;
  return <aside className="w-[440px] shrink-0 flex flex-col bg-[#0e0e12]" data-id="element-5">
      {/* Panel header */}
      <div className="px-6 pt-5 pb-4 border-b border-white/5" data-id="element-6">
        <div className="flex items-center justify-between" data-id="element-7">
          <div className="flex items-center gap-2" data-id="element-8">
            <SparklesIcon className="w-3.5 h-3.5 text-violet-300" data-id="element-9" />
            <span className="text-[11px] uppercase tracking-[0.18em] font-mono text-violet-300/90" data-id="element-10">
              Smart Form
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 font-mono" data-id="element-11">
            <span className="kbd" data-id="element-12">Tab</span>
            <span data-id="element-13">to navigate</span>
          </div>
        </div>
        <h2 className="text-[17px] font-semibold mt-2 tracking-tight" data-id="element-14">
          Auto-detected fields
        </h2>
        <p className="text-[12px] text-zinc-500 mt-0.5" data-id="element-15">
          We found {total} fillable fields. Complete them below — changes sync
          to the PDF live.
        </p>
      </div>

      {/* Fields */}
      <div className="flex-1 overflow-y-auto thin-scroll px-6 py-5 space-y-6" data-id="element-16">
        {sections.map(section => <section key={section} className="space-y-3" data-id="element-17">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-mono text-zinc-500" data-id="element-18">
              <span data-id="element-19">{section}</span>
              <span className="flex-1 h-px bg-white/5" data-id="element-20" />
            </div>
            <div className="space-y-3" data-id="element-21">
              {FIELDS.filter(f => f.section === section).map(field => {
            const isActive = activeId === field.id;
            const filled = field.type === 'signature' ? Boolean(signatureDataUrl) : Boolean(values[field.id]);
            return <div key={field.id} data-id="element-22">
                    <label className="flex items-center justify-between mb-1.5" data-id="element-23">
                      <span className="flex items-center gap-2 text-[12px] text-zinc-300" data-id="element-24">
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-violet-400 dot-pulse" data-id="element-25" />}
                        {!isActive && filled && <span className="w-3.5 h-3.5 rounded-full bg-emerald-400/15 flex items-center justify-center" data-id="element-26">
                            <CheckIcon className="w-2.5 h-2.5 text-emerald-300" strokeWidth={3} data-id="element-27" />
                          </span>}
                        {!isActive && !filled && <span className="w-1.5 h-1.5 rounded-full border border-zinc-600" data-id="element-28" />}
                        {field.label}
                      </span>
                      {isActive && <span className="text-[9px] uppercase tracking-[0.15em] font-mono text-violet-300/80" data-id="element-29">
                          Editing
                        </span>}
                    </label>

                    {field.type === 'signature' ? <SignaturePad value={signatureDataUrl} onChange={url => {
                onSignatureChange(url);
                setActiveId('signature');
              }} active={isActive} data-id="element-30" /> : <input type={field.type === 'date' ? 'text' : field.type === 'email' ? 'email' : 'text'} placeholder={field.placeholder} value={values[field.id] || ''} onChange={e => onChange(field.id, e.target.value)} onFocus={() => setActiveId(field.id)} onBlur={() => setActiveId(null)} className={`w-full bg-[#0a0a0e] rounded-lg px-3.5 py-2.5 text-[13px] text-zinc-100 placeholder:text-zinc-600 border transition-all outline-none ${isActive ? 'border-violet-400/60 shadow-[0_0_0_3px_rgba(167,139,250,0.12)]' : 'border-white/8 hover:border-white/15'}`} data-id="element-31" />}
                  </div>;
          })}
            </div>
          </section>)}
      </div>

      {/* Footer / Download */}
      <div className="px-6 py-4 border-t border-white/5 space-y-3 bg-[#0c0c10]" data-id="element-32">
        <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500" data-id="element-33">
          <span className="flex items-center gap-1.5" data-id="element-34">
            <LockIcon className="w-3 h-3" data-id="element-35" />
            End-to-end encrypted
          </span>
          <span className="flex items-center gap-1.5" data-id="element-36">
            <ShieldCheckIcon className="w-3 h-3" data-id="element-37" />
            ESIGN Act compliant
          </span>
        </div>
        <button onClick={onDownload} disabled={!allFilled} className={`relative w-full py-3.5 rounded-xl font-semibold text-[14px] tracking-tight transition-all overflow-hidden group ${allFilled ? 'bg-violet-500 hover:bg-violet-400 text-white shadow-[0_8px_24px_-6px_rgba(167,139,250,0.55)]' : 'bg-violet-500/20 text-violet-200/60 cursor-not-allowed'}`} data-id="element-38">
          {allFilled && <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-39" />}
          <span className="relative flex items-center justify-center gap-2" data-id="element-40">
            <DownloadIcon className="w-4 h-4" data-id="element-41" />
            {allFilled ? 'Download Signed PDF' : `Fill ${total - filled} more to download`}
          </span>
        </button>
      </div>
    </aside>;
}